import Header from '@/components/Header';
import Footer from '@/components/Footer';
import LogoBigColor from '@/icons/LogoBigColor';
import MobileBottomNav from '@/components/MobileBottomNav';
import { IconArrowLeft as ArrowLeft } from '@tabler/icons-react';
import { PageHero, btnPrimary, pageBg, surface, wrap } from '@/components/ui/design';

export default function TermsConditions() {
    return (
        <div className={`min-h-screen flex flex-col ${pageBg}`}>
            <Header />

            <main className="flex-1">
                <PageHero
                    eyebrow={<LogoBigColor width={160} height={45} />}
                    title="Terms and Conditions"
                    subtitle={<>Please read these terms and conditions carefully before using our platform. By accessing or using our services, you agree to be bound by these terms.</>}
                >
                    <p className="mt-6 inline-flex h-9 items-center rounded-full bg-grey99 px-4 text-sm font-semibold text-grey40 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:text-nightMuted dark:ring-white/10">
                        Last updated: September 2025
                    </p>
                </PageHero>

                <div className={`${wrap} mt-14 pb-20 sm:mt-20`}>
                    <article className="max-w-[720px]">
                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">1. Introduction</h2>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                Welcome to {import.meta.env.VITE_SITE_NAME}, a global platform for discovering, creating, and promoting Iranian events.
                                These Terms and Conditions ("Terms") govern your use of our website and services.
                            </p>
                            <p className="text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                By accessing or using our platform, you agree to be bound by these Terms. If you do not agree to these Terms,
                                please do not use our services.
                            </p>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">2. Acceptance of Terms</h2>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                By using our platform, you acknowledge that you have read, understood, and agree to be bound by these Terms.
                                These Terms constitute a legally binding agreement between you and {import.meta.env.VITE_SITE_NAME}.
                            </p>
                            <p className="text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                We reserve the right to modify these Terms at any time. Your continued use of our services after such modifications
                                constitutes acceptance of the updated Terms.
                            </p>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">3. User Accounts</h2>

                            <h3 className="mt-8 mb-3 text-lg font-bold tracking-[-0.02em] text-grey20 dark:text-nightTextSecondary">3.1 Account Creation</h3>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                To access certain features of our platform, you may need to create an account. When creating an account, you agree to:
                            </p>
                            <ul className="mb-4 list-disc space-y-2 pl-5 text-[17px] leading-[1.75] text-grey40 marker:text-grey70 dark:text-nightMuted dark:marker:text-white/30">
                                <li>Provide accurate, current, and complete information</li>
                                <li>Maintain and update your account information</li>
                                <li>Keep your password secure and confidential</li>
                                <li>Accept responsibility for all activities under your account</li>
                            </ul>

                            <h3 className="mt-8 mb-3 text-lg font-bold tracking-[-0.02em] text-grey20 dark:text-nightTextSecondary">3.2 Account Security</h3>
                            <p className="text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                You are responsible for maintaining the confidentiality of your account credentials and for all activities
                                that occur under your account. You must notify us immediately of any unauthorized use of your account.
                            </p>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">4. Use of Services</h2>

                            <h3 className="mt-8 mb-3 text-lg font-bold tracking-[-0.02em] text-grey20 dark:text-nightTextSecondary">4.1 Permitted Use</h3>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                You may use our services for lawful purposes only. You agree to use our platform in accordance with these Terms
                                and all applicable laws and regulations.
                            </p>

                            <h3 className="mt-8 mb-3 text-lg font-bold tracking-[-0.02em] text-grey20 dark:text-nightTextSecondary">4.2 Prohibited Activities</h3>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                You agree not to engage in any of the following prohibited activities:
                            </p>
                            <ul className="mb-4 list-disc space-y-2 pl-5 text-[17px] leading-[1.75] text-grey40 marker:text-grey70 dark:text-nightMuted dark:marker:text-white/30">
                                <li>Violating any applicable laws or regulations</li>
                                <li>Infringing on intellectual property rights</li>
                                <li>Transmitting harmful or malicious code</li>
                                <li>Attempting to gain unauthorized access to our systems</li>
                                <li>Interfering with the proper functioning of our platform</li>
                                <li>Creating fake accounts or impersonating others</li>
                                <li>Spamming or sending unsolicited communications</li>
                            </ul>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">5. Event Listings and Tickets</h2>

                            <h3 className="mt-8 mb-3 text-lg font-bold tracking-[-0.02em] text-grey20 dark:text-nightTextSecondary">5.1 Event Information</h3>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                We provide a platform for event organizers to list their events. While we strive to ensure accuracy,
                                we do not guarantee the accuracy, completeness, or reliability of event information provided by third parties.
                            </p>

                            <h3 className="mt-8 mb-3 text-lg font-bold tracking-[-0.02em] text-grey20 dark:text-nightTextSecondary">5.2 Ticket Purchases</h3>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                When you purchase tickets through our platform:
                            </p>
                            <ul className="mb-4 list-disc space-y-2 pl-5 text-[17px] leading-[1.75] text-grey40 marker:text-grey70 dark:text-nightMuted dark:marker:text-white/30">
                                <li>You enter into a contract with the event organizer, not with us</li>
                                <li>We act as a payment processor and platform provider</li>
                                <li>Event organizers are responsible for event delivery and any issues</li>
                                <li>Refund policies are determined by the event organizer</li>
                            </ul>

                            <h3 className="mt-8 mb-3 text-lg font-bold tracking-[-0.02em] text-grey20 dark:text-nightTextSecondary">5.3 Event Cancellations</h3>
                            <p className="text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                In case of event cancellation or postponement, refund policies are determined by the event organizer.
                                We will assist in facilitating refunds according to the organizer's policies.
                            </p>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">6. Payment Terms</h2>

                            <h3 className="mt-8 mb-3 text-lg font-bold tracking-[-0.02em] text-grey20 dark:text-nightTextSecondary">6.1 Payment Processing</h3>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                We use secure payment processors to handle transactions. By making a purchase, you agree to:
                            </p>
                            <ul className="mb-4 list-disc space-y-2 pl-5 text-[17px] leading-[1.75] text-grey40 marker:text-grey70 dark:text-nightMuted dark:marker:text-white/30">
                                <li>Provide accurate payment information</li>
                                <li>Authorize charges for the full amount</li>
                                <li>Pay all applicable fees and taxes</li>
                                <li>Accept our refund and cancellation policies</li>
                            </ul>

                            <h3 className="mt-8 mb-3 text-lg font-bold tracking-[-0.02em] text-grey20 dark:text-nightTextSecondary">6.2 Fees and Charges</h3>
                            <p className="text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                We may charge service fees for using our platform. All fees are clearly displayed before purchase.
                                Prices are subject to change, but existing bookings will not be affected by price changes.
                            </p>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">7. Intellectual Property</h2>

                            <h3 className="mt-8 mb-3 text-lg font-bold tracking-[-0.02em] text-grey20 dark:text-nightTextSecondary">7.1 Our Content</h3>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                All content on our platform, including text, graphics, logos, images, and software, is the property of
                                {import.meta.env.VITE_SITE_NAME} or its licensors and is protected by copyright and other intellectual property laws.
                            </p>

                            <h3 className="mt-8 mb-3 text-lg font-bold tracking-[-0.02em] text-grey20 dark:text-nightTextSecondary">7.2 User Content</h3>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                By posting content on our platform, you grant us a non-exclusive, royalty-free license to use, modify,
                                and distribute your content in connection with our services.
                            </p>

                            <h3 className="mt-8 mb-3 text-lg font-bold tracking-[-0.02em] text-grey20 dark:text-nightTextSecondary">7.3 Third-Party Content</h3>
                            <p className="text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                We respect intellectual property rights. If you believe your rights have been infringed, please contact us
                                with details of the alleged infringement.
                            </p>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">8. Privacy and Data Protection</h2>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                Your privacy is important to us. Our collection and use of personal information is governed by our
                                Privacy Policy, which is incorporated into these Terms by reference.
                            </p>
                            <p className="text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                By using our services, you consent to the collection and use of your information as described in our Privacy Policy.
                            </p>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">9. Disclaimers</h2>

                            <h3 className="mt-8 mb-3 text-lg font-bold tracking-[-0.02em] text-grey20 dark:text-nightTextSecondary">9.1 Service Availability</h3>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                We strive to provide reliable services, but we do not guarantee that our platform will be available
                                at all times or free from errors, viruses, or other harmful components.
                            </p>

                            <h3 className="mt-8 mb-3 text-lg font-bold tracking-[-0.02em] text-grey20 dark:text-nightTextSecondary">9.2 Third-Party Services</h3>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                Our platform may contain links to third-party websites or services. We are not responsible for the content,
                                privacy policies, or practices of these third parties.
                            </p>

                            <h3 className="mt-8 mb-3 text-lg font-bold tracking-[-0.02em] text-grey20 dark:text-nightTextSecondary">9.3 Event Quality</h3>
                            <p className="text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                We do not guarantee the quality, safety, or legality of events listed on our platform. Event organizers
                                are responsible for their events and compliance with applicable laws.
                            </p>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">10. Limitation of Liability</h2>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                To the maximum extent permitted by law, {import.meta.env.VITE_SITE_NAME} and its affiliates shall not be liable for:
                            </p>
                            <ul className="mb-4 list-disc space-y-2 pl-5 text-[17px] leading-[1.75] text-grey40 marker:text-grey70 dark:text-nightMuted dark:marker:text-white/30">
                                <li>Any indirect, incidental, or consequential damages</li>
                                <li>Loss of profits, data, or business opportunities</li>
                                <li>Damages resulting from event cancellations or changes</li>
                                <li>Issues with third-party services or content</li>
                                <li>Unauthorized access to your account</li>
                            </ul>
                            <p className="text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                Our total liability shall not exceed the amount you paid for the specific service giving rise to the claim.
                            </p>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">11. Indemnification</h2>
                            <p className="text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                You agree to indemnify and hold harmless {import.meta.env.VITE_SITE_NAME} and its affiliates from any claims,
                                damages, or expenses arising from your use of our services, violation of these Terms, or infringement
                                of any rights of another party.
                            </p>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">12. Termination</h2>

                            <h3 className="mt-8 mb-3 text-lg font-bold tracking-[-0.02em] text-grey20 dark:text-nightTextSecondary">12.1 Termination by You</h3>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                You may terminate your account at any time by contacting us or using the account deletion feature
                                in your account settings.
                            </p>

                            <h3 className="mt-8 mb-3 text-lg font-bold tracking-[-0.02em] text-grey20 dark:text-nightTextSecondary">12.2 Termination by Us</h3>
                            <p className="text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                We may suspend or terminate your account if you violate these Terms or engage in prohibited activities.
                                We will provide reasonable notice when possible, except in cases of serious violations.
                            </p>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">13. Governing Law</h2>
                            <p className="text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                These Terms are governed by the laws of the State of California, United States, without regard to
                                conflict of law principles. Any disputes arising from these Terms will be resolved in the courts
                                of Orange County, California.
                            </p>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">14. Changes to Terms</h2>
                            <p className="text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                We may update these Terms from time to time. We will notify you of any material changes by posting
                                the updated Terms on our website and updating the "Last updated" date. Your continued use of our
                                services after such changes constitutes acceptance of the updated Terms.
                            </p>
                        </section>

                        <section className="mb-14">
                            <h2 className="mb-5 text-[clamp(24px,3vw,30px)] font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">15. Contact Us</h2>
                            <p className="mb-4 text-[17px] leading-[1.75] text-grey40 dark:text-nightMuted">
                                If you have any questions about these Terms and Conditions, please contact us:
                            </p>
                            <div className={`${surface} p-6 sm:p-8`}>
                                <div className="space-y-3">
                                    <div>
                                        <strong className="font-semibold text-grey10 dark:text-white">Email:</strong>
                                        <span className="ml-2 break-words text-grey40 dark:text-nightMuted">admin@nexalogix.com</span>
                                    </div>
                                    <div>
                                        <strong className="font-semibold text-grey10 dark:text-white">Phone:</strong>
                                        <span className="ml-2 break-words text-grey40 dark:text-nightMuted">(949) 431-6160</span>
                                    </div>
                                    <div>
                                        <strong className="font-semibold text-grey10 dark:text-white">Address:</strong>
                                        <span className="ml-2 break-words text-grey40 dark:text-nightMuted">Aliso Viejo, CA 92656</span>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </article>

                    <div className="mt-16">
                        <a
                            href="/"
                            className={btnPrimary}
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back to Home
                        </a>
                    </div>
                </div>
            </main>

            <Footer hasMobileNav />
            <MobileBottomNav />
        </div>
    );
}
